import { supabase } from "@/integrations/supabase/client";
import { generateEventId, generateDeterministicEventId, detectCountry } from "@/lib/facebookPixel";

// ============================================
// GLOBAL DECLARATIONS
// ============================================

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

// ============================================
// SAFE STORAGE HELPERS
// ============================================

function lsSet(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* silent */ }
}

function lsGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function ssSet(key: string, value: string) {
  try { sessionStorage.setItem(key, value); } catch { /* silent */ }
}

function ssGet(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch { return null; }
}

// ============================================
// GA4 CLIENT ID (from _ga cookie + fallbacks)
// ============================================

const LS_GA_CLIENT_ID_BACKUP = '_ga_client_id_backup';
const LS_GA_SYNTHETIC_CLIENT_ID = '_ga_synthetic_client_id';

/**
 * Extract GA4 client_id from _ga cookie.
 * Falls back to localStorage backup → synthetic ID.
 * Ensures GA4 MP events are NEVER dropped due to missing client_id.
 */
export function getGA4ClientId(): string | null {
  try {
    const gaCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('_ga='));
    if (gaCookie) {
      // _ga cookie format: GA1.1.XXXXXXXXXX.XXXXXXXXXX
      const parts = gaCookie.split('.');
      if (parts.length >= 4) {
        const clientId = `${parts[2]}.${parts[3]}`;
        // Sync real client_id to backup storage
        lsSet(LS_GA_CLIENT_ID_BACKUP, clientId);
        ssSet(LS_GA_CLIENT_ID_BACKUP, clientId);
        return clientId;
      }
    }

    // Cookie missing — try localStorage backup
    const lsBackup = lsGet(LS_GA_CLIENT_ID_BACKUP);
    if (lsBackup) return lsBackup;

    // Try sessionStorage backup (survives localStorage clearing)
    const ssBackup = ssGet(LS_GA_CLIENT_ID_BACKUP);
    if (ssBackup) {
      lsSet(LS_GA_CLIENT_ID_BACKUP, ssBackup);
      return ssBackup;
    }

    // Last resort — create synthetic client_id (like synthetic FBP)
    return getOrCreateSyntheticGA4ClientId();
  } catch {
    return getOrCreateSyntheticGA4ClientId();
  }
}

/**
 * Generate a synthetic GA4 client_id when the _ga cookie is unavailable.
 * Format matches GA4: <timestamp>.<random_number>
 * Stored ONLY in localStorage (never as cookie — let gtag.js set its own).
 */
function getOrCreateSyntheticGA4ClientId(): string {
  const existing = lsGet(LS_GA_SYNTHETIC_CLIENT_ID);
  if (existing) return existing;

  const syntheticId = `${Math.floor(Date.now() / 1000)}.${Math.floor(Math.random() * 2147483647 + 1000000000)}`;
  lsSet(LS_GA_SYNTHETIC_CLIENT_ID, syntheticId);
  return syntheticId;
}

// ============================================
// GA4 SESSION ID (from _ga_<container> cookie + fallbacks)
// ============================================

const LS_GA_SESSION_ID_BACKUP = '_ga_session_id_backup';

/**
 * Extract GA4 session_id from _ga_<container> cookie.
 * Falls back to localStorage → sessionStorage backup.
 * Ensures session continuity even when cookies are cleared.
 */
export function getGA4SessionId(): string | null {
  try {
    const cookies = document.cookie.split('; ');
    const gaSessionCookie = cookies.find(row => row.startsWith('_ga_'));
    if (gaSessionCookie) {
      // Format: _ga_XXXXX=GS1.1.SESSION_ID.SESSION_COUNT...
      const parts = gaSessionCookie.split('.');
      if (parts.length >= 3) {
        const sessionId = parts[2];
        // Sync real session_id to backup storage
        lsSet(LS_GA_SESSION_ID_BACKUP, sessionId);
        ssSet(LS_GA_SESSION_ID_BACKUP, sessionId);
        return sessionId;
      }
    }

    // Cookie missing — try localStorage backup
    const lsBackup = lsGet(LS_GA_SESSION_ID_BACKUP);
    if (lsBackup) return lsBackup;

    // Try sessionStorage backup
    const ssBackup = ssGet(LS_GA_SESSION_ID_BACKUP);
    if (ssBackup) {
      lsSet(LS_GA_SESSION_ID_BACKUP, ssBackup);
      return ssBackup;
    }

    return null;
  } catch {
    return null;
  }
}

// ============================================
// SEND TO GA4 MEASUREMENT PROTOCOL (Server-side)
// ============================================

async function sendToGA4MP(eventData: {
  name: string;
  params: Record<string, unknown>;
  browser_gtag_fired?: boolean;
}) {
  try {
    const clientId = getGA4ClientId();
    if (!clientId) {
      // This should never happen now that we have synthetic fallback,
      // but guard against edge cases (e.g. localStorage completely disabled)
      console.warn('GA4 MP: No client_id available, skipping server-side event');
      return;
    }

    const sessionId = getGA4SessionId();

    // Detect country for logging (reuse facebookPixel's detectCountry)
    let countryCode: string | null = null;
    try {
      const { detectCountry } = await import('@/lib/facebookPixel');
      countryCode = detectCountry();
    } catch { /* silent */ }

    // Import traffic source detection
    let trafficSource: string | null = null;
    try {
      const { getTrafficSource } = await import('@/lib/facebookPixel');
      trafficSource = getTrafficSource();
    } catch { /* silent */ }

    const response = await supabase.functions.invoke('google-analytics-mp', {
      body: {
        client_id: clientId,
        country_code: countryCode,
        traffic_source: trafficSource,
        browser_gtag_fired: eventData.browser_gtag_fired ?? false,
        events: [{
          name: eventData.name,
          params: {
            ...eventData.params,
            ...(sessionId ? { session_id: sessionId } : {}),
            engagement_time_msec: '100',
          },
        }],
      },
    });

    if (response.error) {
      console.error('GA4 MP Error:', response.error);
    } else {
      console.log('GA4 MP Success:', eventData.name);
    }
  } catch (error) {
    console.error('GA4 MP Request Failed:', error);
  }
}

// Browser GA4 logging is now handled server-side by the google-analytics-mp edge function
// using the browser_gtag_fired flag. This avoids RLS issues with client-side inserts.

// ============================================
// TRACKING FUNCTIONS
// ============================================

// Initialize GA4 with measurement ID
export function initializeGA4(measurementId: string) {
  if (window.gtag) {
    window.gtag('config', measurementId, {
      send_page_view: false, // We manage page views manually
    });
    console.log('GA4 initialized:', measurementId);
  } else {
    console.warn('gtag not loaded - GA4 script may be blocked');
  }
}

// Track Page View
// NOTE: page_view is NOT sent via GA4 MP — only browser gtag.js handles it.
// Sending page_view server-side creates duplicate sessions with wrong attribution
// (e.g. Facebook traffic misattributed as google/direct).
export async function trackGA4PageView(sharedEventId?: string) {
  if (!window.gtag) return;

  const eventId = sharedEventId || await generateEventId('page_view', window.location.pathname);

  window.gtag('event', 'page_view', {
    page_title: document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
    event_id: eventId,
  });
}

// Track View Item (Product Page)
export async function trackGA4ViewContent(product: {
  id: string;
  title: string;
  price: number;
  currency: string;
  category?: string;
}, sharedEventId?: string) {
  const eventId = sharedEventId || await generateEventId('view_item', product.id);

  const itemParams = {
    currency: product.currency,
    value: product.price,
    items: [{
      item_id: product.id,
      item_name: product.title,
      item_category: product.category || 'Skincare',
      price: product.price,
      quantity: 1,
    }],
    event_id: eventId,
  };

  const browserFired = !!window.gtag;
  if (window.gtag) {
    window.gtag('event', 'view_item', itemParams);
  }

  sendToGA4MP({
    name: 'view_item',
    params: {
      currency: product.currency,
      value: product.price,
      items: itemParams.items,
      event_id: eventId,
    },
    browser_gtag_fired: browserFired,
  });
}

// Track Add to Cart
export async function trackGA4AddToCart(item: {
  productId: string;
  variantId: string;
  title: string;
  price: number;
  currency: string;
  quantity: number;
  category?: string;
}, sharedEventId?: string) {
  const eventId = sharedEventId || await generateEventId('add_to_cart', `${item.productId}_${Date.now()}`);

  const itemParams = {
    currency: item.currency,
    value: item.price * item.quantity,
    items: [{
      item_id: item.productId,
      item_name: item.title,
      item_category: item.category || 'Skincare',
      item_variant: item.variantId,
      price: item.price,
      quantity: item.quantity,
    }],
    event_id: eventId,
  };

  const browserFired = !!window.gtag;
  if (window.gtag) {
    window.gtag('event', 'add_to_cart', itemParams);
  }

  sendToGA4MP({
    name: 'add_to_cart',
    params: {
      currency: item.currency,
      value: item.price * item.quantity,
      items: itemParams.items,
      event_id: eventId,
    },
    browser_gtag_fired: browserFired,
  });
}

// Track Begin Checkout
export async function trackGA4InitiateCheckout(cart: {
  items: Array<{
    productId: string;
    variantId: string;
    title?: string;
    price: number;
    quantity: number;
  }>;
  totalValue: number;
  currency: string;
  cartId?: string;
}, sharedEventId?: string) {
  const eventId = sharedEventId || await generateEventId('begin_checkout', cart.cartId || Date.now().toString());

  const ga4Items = cart.items.map(item => ({
    item_id: item.productId,
    item_name: item.title || item.productId,
    item_variant: item.variantId,
    price: item.price,
    quantity: item.quantity,
  }));

  const checkoutParams = {
    currency: cart.currency,
    value: cart.totalValue,
    items: ga4Items,
    event_id: eventId,
  };

  const browserFired = !!window.gtag;
  if (window.gtag) {
    window.gtag('event', 'begin_checkout', checkoutParams);
  }

  sendToGA4MP({
    name: 'begin_checkout',
    params: {
      currency: cart.currency,
      value: cart.totalValue,
      items: ga4Items,
      event_id: eventId,
    },
    browser_gtag_fired: browserFired,
  });
}

// ============================================
// SURVEY FUNNEL TRACKING
// ============================================

export function trackSurveyEvent(
  action: string,
  params: Record<string, string | number>
) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', action, params);
    console.log('GA4 Survey Event:', action, params);
  }
}

// Track Purchase (kept for reference — primarily handled server-side via Shopify webhook)
export async function trackGA4Purchase(order: {
  orderId: string;
  items: Array<{
    productId: string;
    variantId: string;
    price: number;
    quantity: number;
  }>;
  totalValue: number;
  currency: string;
  createdAt?: number;
}) {
  // Use deterministic event_id (timestamp=0) to match webhook deduplication
  const eventId = await generateDeterministicEventId('Purchase', order.orderId, 0);

  const ga4Items = order.items.map(item => ({
    item_id: item.productId,
    item_variant: item.variantId,
    price: item.price,
    quantity: item.quantity,
  }));

  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: order.orderId,
      currency: order.currency,
      value: order.totalValue,
      items: ga4Items,
      event_id: eventId,
    });
  }
}
